import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { clx } from '@medusajs/ui';

import AppLink, { type AppLinkProps } from '@adapters/app-link';
import { omitKeys, type ClassValue } from '@modules/common/types';

type ButtonVariant = 'primary' | 'outlined' | 'outlined-warning' | 'contrast';

type ButtonBaseProps = {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  children: ReactNode;
  className?: ClassValue;
};

type ButtonAsButton = ButtonBaseProps &
  Omit<
    ComponentPropsWithoutRef<'button'>,
    keyof ButtonBaseProps | 'as'
  > & {
    as?: 'button';
    href?: never;
  };

type ButtonAsLink = ButtonBaseProps &
  Omit<AppLinkProps, keyof ButtonBaseProps | 'as' | 'disabled'> & {
    as: 'link';
    href: string;
    disabled?: never;
  };

type ButtonProps = ButtonAsButton | ButtonAsLink;

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'rounded px-4 py-2 items-center justify-center bg-primary-50 text-center text-base font-bold text-white-10 transition-colors ease-in hover:bg-primary-40',
  outlined:
    'rounded-sm border border-black-10 px-3 py-1 text-black-10 transition-colors ease-in hover:border-primary-20 hover:text-primary-30',
  'outlined-warning':
    'rounded-sm border border-orange-30 px-3 py-1 text-orange-30 transition-colors ease-in hover:border-orange-30 hover:text-orange-30',
  contrast:
    'rounded-full border border-black px-4 py-2 transition-colors duration-200 ease-in hover:bg-black hover:text-white',
};

const disabledStyles = 'cursor-default bg-gray-300 hover:bg-gray-300';

/** Keys to strip from props before spreading onto the rendered element. */
const customPropKeys = new Set(['as', 'variant', 'fullWidth'] as const);

const Button = (props: ButtonProps) => {
  const { variant = 'primary', fullWidth, className, children } = props;

  const baseClassName = clx(
    variantStyles[variant],
    fullWidth && 'w-full',
    className
  );

  if (props.as === 'link') {
    const linkProps = omitKeys(props, customPropKeys) as Omit<
      AppLinkProps,
      'as' | 'variant' | 'fullWidth'
    >;

    return (
      <AppLink
        {...linkProps}
        className={clx(baseClassName, fullWidth && 'block')}
      >
        {children}
      </AppLink>
    );
  }

  const { disabled, ...buttonRest } = omitKeys(props, customPropKeys) as Omit<
    ComponentPropsWithoutRef<'button'>,
    'as' | 'variant' | 'fullWidth'
  >;

  return (
    <button
      {...buttonRest}
      disabled={disabled}
      className={clx(baseClassName, disabled && disabledStyles)}
    >
      {children}
    </button>
  );
};

export default Button;
export type { ButtonVariant, ButtonProps };
